import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_MEDIUM_SCREEN_850 } from 'client/constants/mediaQueries';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import {
  USER_ROLE_PARTNER,
  USER_ROLE_ADMIN,
} from 'common/userRoles';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLoadingSpinner from 'client/components/SrLoadingSpinner/SrLoadingSpinner';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import {
  SCREEN_ID_PARTNER_OVERVIEW,
} from 'client/constants/screenIds';

import postWithState from 'client/actions/postWithState';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withApiState from 'client/helpers/withApiState';

import {
  SR_API_ACTION_PARTNER_SEND_MOBILE_APP_DOWNLOAD_LINK,
} from 'api/actionTypes';

const formFields = [
  {
    id: 'partnerPhoneNumber',
    type: 'phone',
    validations: ['required'],
    properties: {
      placeholder: '(xxx) xxx-xxxx',
    },
  },
];

const AppPreview = () => {
  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_850,
  });

  return (
    <img
      alt='app preview'
      src='/images/app-preview-yellow-background.png'
      style={{ display: isMediumScreen ? 'none' : null }}
    />
  );
};

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_OVERVIEW)
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
    USER_ROLE_PARTNER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  sendMobileAppDownloadLinkApiState: {
    action: SR_API_ACTION_PARTNER_SEND_MOBILE_APP_DOWNLOAD_LINK,
  },
})
class PartnerDownloadMobileAppPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Download Partner App | Sunroom Rentals',
  })

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    sendMobileAppDownloadLinkApiState: PropTypes.object,
  }

  constructor(props) {
    super(props);

    this.state = {
      isSent: false,
    };
  }

  _onSendAppDownloadLink = (values, formikBag) => {
    const { dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SEND_MOBILE_APP_DOWNLOAD_LINK,
      payload: {
        phone: values.partnerPhoneNumber,
      },
      onSuccess: () => {
        formikBag.resetForm();
        this.setState({ isSent: true });
        setTimeout(() => {
          this.setState({ isSent: false });
        }, 5000);
      },
    });
  }

  render() {
    const { sendMobileAppDownloadLinkApiState } = this.props;

    return (
      <SrBox
        backgroundImage='url(/images/faded-yellow-background.jpg)'
        backgroundSize='cover'
        backgroundRepeat='no-repeat'
        backgroundPosition='center'
        width='100vw'
        height='auto'
        minHeight='100vh'
      >
        <SrFlex
          justify='center'
          align='center'
          maxWidth='1400px'
          margin='0 auto'
          wrap='wrap'
          height='100%'
          minHeight='100vh'
          width='100%'
          padding='30px'
          boxSizing='border-box'
        >
          <SrBox
            backgroundColor='white'
            borderRadius='32px'
            boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
            padding='65px'
            maxWidth='561px'
            boxSizing='border-box'
          >
            <SrBox marginBottom='53px'>
              <img
                alt='logo'
                src='/images/sunroom-logo-yellow.svg'
                width='200px'
                height='50px'
              />
            </SrBox>
            <SrBox marginBottom='24px'>
              <SrText
                font='greycliff'
                weight='800'
                size='40'
                lineHeight='48px'
                color='black'
              >
                Download the Sunroom Leasing App!
              </SrText>
            </SrBox>
            <SrBox>
              <SrText
                font='greycliff'
                weight='600'
                size='16'
                lineHeight='25px'
                color='greyDark'
                opacity='0.5'
              >
                Enter your phone number and we’ll text you a link to the Sunroom Leasing App!
              </SrText>
            </SrBox>
            <Formik
              enableReinitialize
              validateOnChange={false}
              initialValues={createInitialValues({
                formFields: formFields,
                defaultValues: {},
              })}
              onSubmit={this._onSendAppDownloadLink}
              validate={createFormValidationFn(formFields)}
              render={(formikBag) => {
                return (
                  <div>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                    <SrBox marginTop='32px'>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        hasBoxShadow
                        onClick={formikBag.handleSubmit}
                      >
                        {this.state.isSent
                          ? 'Link Sent!'
                          : sendMobileAppDownloadLinkApiState.inProgress
                            ? <SrLoadingSpinner />
                            : 'Send Link'
                        }
                      </SrButton>
                    </SrBox>
                    <SrBox marginTop='56px'>
                      <a
                        href='https://apps.apple.com/us/app/sunroom-leasing-for-realtors/id1591663241'
                        target='_blank'
                        rel='noreferrer'
                      >
                        <img
                          alt='ios badge'
                          src='/images/ios-badge.svg'
                          height='48px'
                          width='auto'
                          style={{ display: 'inline-block' }}
                        />
                      </a>
                      <a
                        href='https://play.google.com/store/apps/details?id=com.sunroomrentals.partners'
                        target='_blank'
                        rel='noreferrer'
                      >
                        <img
                          alt='android badge'
                          src='/images/android-badge.svg'
                          height='48px'
                          width='auto'
                          style={{ display: 'inline-block', marginLeft: '16px' }}
                        />
                      </a>
                    </SrBox>
                  </div>
                );
              }}
            />
          </SrBox>
          <AppPreview />
        </SrFlex>
      </SrBox>
    );
  }
}

PartnerDownloadMobileAppPage.displayName = 'PartnerDownloadMobileAppPage';

export default PartnerDownloadMobileAppPage;
